import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { formatDate, formatPhone } from '../utils/dateUtils';

const sexLabels = { M: 'Masculino', F: 'Feminino', Outro: 'Outro' };

export default function PatientDashboard({ patient, sessions, onPatientUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [showDiagModal, setShowDiagModal] = useState(false);
  const [diagForm, setDiagForm] = useState({ queixaPrincipal: '', diagnosticoFisioterapeutico: '', planoCuidados: '' });
  const [savingDiag, setSavingDiag] = useState(false);
  const navigate = useNavigate();

  const aguardandoAvaliacao = patient?.status === 'aguardando_avaliacao';

  useEffect(() => {
    if (patient) {
      setFormData({
        nome: patient.nome,
        telefone: patient.telefone,
        email: patient.email || '',
        dateOfBirth: patient.dateOfBirth || '',
        diagnosisTitle: patient.diagnosisTitle || '',
        diagnosisDescription: patient.diagnosisDescription || '',
      });
      setIsEditing(false);
    } else {
      setFormData(null);
    }
  }, [patient]);

  const handleChange = (e) => {
    if (formData) setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      if (!formData || !formData.nome || !formData.telefone) {
        return toast.error('Nome e telefone são obrigatórios.');
      }
      const promise = api.put(`/patients/${patient._id}`, formData);
      toast.promise(promise, {
        loading: 'Salvando alterações...',
        success: 'Dados do paciente atualizados!',
        error: 'Falha ao salvar.'
      });
      const updatedPatient = await promise;
      setIsEditing(false);
      if (onPatientUpdate) onPatientUpdate(updatedPatient.data.data);
    } catch (error) {}
  };

  const handleRealizarAvaliacao = () => {
    setDiagForm({ queixaPrincipal: '', diagnosticoFisioterapeutico: '', planoCuidados: '' });
    setShowDiagModal(true);
  };

  const handleSalvarDiagnostico = async () => {
    if (!diagForm.diagnosticoFisioterapeutico.trim()) {
      return toast.error('Diagnóstico é obrigatório');
    }
    setSavingDiag(true);
    try {
      await api.post('/avaliacoes', {
        pacienteId: patient._id,
        dataAvaliacao: new Date().toISOString().split('T')[0],
        queixaPrincipal: diagForm.queixaPrincipal || 'Não informado',
        diagnosticoFisioterapeutico: diagForm.diagnosticoFisioterapeutico,
        planoCuidados: diagForm.planoCuidados || '',
      });
      toast.success('Diagnóstico salvo! Paciente em tratamento.');
      setShowDiagModal(false);
      if (onPatientUpdate) {
        onPatientUpdate({
          ...patient,
          status: 'em_tratamento',
          diagnosisTitle: diagForm.diagnosticoFisioterapeutico,
          diagnosisDescription: diagForm.planoCuidados,
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar diagnóstico');
    } finally {
      setSavingDiag(false);
    }
  };

  if (!patient) {
    return <div className="p-4 text-center text-gray-500">Use a busca acima para encontrar e selecionar um paciente.</div>;
  }
  if (!formData) {
    return <div className="p-4 text-center text-gray-500">Carregando dados do paciente...</div>;
  }

  return (
    <div className="space-y-6 p-1">

      {/* Header com badge de status */}
      <div className="flex justify-between items-center border-b pb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap flex-1">
            <h4 className="text-lg font-semibold text-gray-800">Dados do Paciente</h4>
            {patient?.clinicaOrigemId && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full border border-blue-200">
                Paciente da Clínica
              </span>
            )}
          </div>
          {aguardandoAvaliacao && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full border border-yellow-200">
              Aguardando Avaliação
            </span>
          )}
          {patient?.status === 'em_tratamento' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full border border-green-200">
              Em Tratamento
            </span>
          )}
          {patient?.status === 'concluido' && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full border border-blue-200">
              Tratamento Concluído
            </span>
          )}
        </div>
        {isEditing ? (
          <div className="flex gap-2">
            <button onClick={handleSave} className="bg-green-500 text-white px-3 py-1 rounded text-sm">Salvar</button>
            <button onClick={() => setIsEditing(false)} className="bg-gray-300 px-3 py-1 rounded text-sm">Cancelar</button>
          </div>
        ) : (
          <button onClick={() => setIsEditing(true)} className="bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700">Editar</button>
        )}
      </div>

      {/* Dados pessoais */}
      <div className="text-sm space-y-2 text-gray-700">
        <p><strong>Nome:</strong> {isEditing ? <input name="nome" value={formData.nome} onChange={handleChange} className="border p-1 w-full rounded"/> : patient.nome}</p>
        <p><strong>Telefone:</strong> {isEditing ? <input name="telefone" value={formData.telefone} onChange={handleChange} className="border p-1 w-full rounded"/> : formatPhone(patient.telefone)}</p>
        <p><strong>Email:</strong> {isEditing ? <input name="email" value={formData.email} onChange={handleChange} className="border p-1 w-full rounded"/> : (patient.email || 'Não informado')}</p>
        <p><strong>Nascimento:</strong> {isEditing ? <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} className="border p-1 w-full rounded"/> : formatDate(patient.dateOfBirth)}</p>
        <p><strong>Sexo:</strong> {sexLabels[patient.sex] || patient.sex}</p>
      </div>

      {/* Diagnóstico */}
      <div>
        <h4 className="text-lg font-semibold text-gray-800 border-b pb-2">Diagnóstico</h4>
        {isEditing ? (
          <div className="space-y-2 mt-2">
            <input name="diagnosisTitle" placeholder="Título do Diagnóstico" value={formData.diagnosisTitle} onChange={handleChange} className="border p-1 w-full rounded font-semibold"/>
            <textarea name="diagnosisDescription" placeholder="Descrição do diagnóstico" value={formData.diagnosisDescription} onChange={handleChange} className="border p-1 w-full rounded" rows="3"/>
          </div>
        ) : (
          <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 mt-2">
            <p className="font-bold text-yellow-900">{patient.diagnosisTitle || 'Não informado'}</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1">{patient.diagnosisDescription || 'Nenhuma descrição.'}</p>
          </div>
        )}
      </div>

      {/* Bloqueio se aguardando avaliação */}
      {aguardandoAvaliacao && (
        <div className="border border-yellow-200 bg-yellow-50 rounded-xl p-4 text-center space-y-3">
          <p className="text-sm text-yellow-800 font-medium">
            Este paciente ainda não possui avaliação inicial. Realize a avaliação para iniciar o tratamento.
          </p>
          <button
            onClick={handleRealizarAvaliacao}
            className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium rounded-xl transition-colors"
          >
            Realizar Avaliação
          </button>
        </div>
      )}

      {/* Histórico de Sessões */}
      <div>
        <h4 className="text-lg font-semibold border-b pb-2 mb-3 text-gray-800">Histórico de Sessões</h4>
        <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
          {sessions && sessions.length > 0 ? (
            sessions.map((session) => (
              <div key={session._id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="font-bold text-gray-900">Sessão {session.sessionNumber}</p>
                <p className="text-xs text-gray-500 mb-2">Data: {formatDate(session.sessionDate)}</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{session.evolutionDescription}</p>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">Nenhuma sessão encontrada para este paciente.</p>
          )}
        </div>
      </div>

      {/* Modal diagnóstico rápido */}
      {showDiagModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 relative shadow-xl">
            <button
              onClick={() => setShowDiagModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>

            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-yellow-100 rounded-xl">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Diagnóstico Fisioterapêutico</h3>
                <p className="text-xs text-gray-400">{patient.nome}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Queixa Principal</label>
                <input
                  type="text"
                  value={diagForm.queixaPrincipal}
                  onChange={e => setDiagForm(f => ({ ...f, queixaPrincipal: e.target.value }))}
                  placeholder="Ex: Dor lombar há 3 semanas"
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-yellow-400 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título do Diagnóstico <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={diagForm.diagnosticoFisioterapeutico}
                  onChange={e => setDiagForm(f => ({ ...f, diagnosticoFisioterapeutico: e.target.value }))}
                  placeholder="Ex: Lombalgia"
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-yellow-400 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição Detalhada</label>
                <textarea
                  value={diagForm.planoCuidados}
                  onChange={e => setDiagForm(f => ({ ...f, planoCuidados: e.target.value }))}
                  placeholder="Descrição detalhada do que o paciente está sentindo..."
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-yellow-400 outline-none resize-none"
                />
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setShowDiagModal(false)}
                  className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSalvarDiagnostico}
                  disabled={savingDiag}
                  className="flex-1 py-2.5 bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-300 text-white text-sm font-medium rounded-xl transition-colors"
                >
                  {savingDiag ? 'Salvando...' : 'Salvar Diagnóstico'}
                </button>
              </div>

              <p className="text-xs text-gray-400 text-center">
                Quer preencher a ficha completa?{' '}
                <button
                  onClick={() => { setShowDiagModal(false); navigate(`/avaliacao?patientId=${patient._id}`); }}
                  className="text-blue-500 hover:underline"
                >
                  Abrir ficha de avaliação
                </button>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}